import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Header } from '@/components/layout/Header'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useAuth } from '@/hooks/useAuth'
import {
  changeAccountPassword,
  unlinkAccountTelegram,
  updateAccountPushover,
  updateAccountPreferences,
  updateAccountTelegramAliases,
} from '@/lib/api'
import type { User } from '@/types'
import { LogOut, Key, Bell, Eye, EyeOff, Link2, Send, Trash2 } from 'lucide-react'

export default function Account() {
  const { user, isAdmin, logout, isLoggingOut } = useAuth()
  const queryClient = useQueryClient()

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPasswords, setShowPasswords] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [isChangingPassword, setIsChangingPassword] = useState(false)

  // Pushover settings state
  const [pushoverUserKey, setPushoverUserKey] = useState('')
  const [pushoverError, setPushoverError] = useState('')
  const [pushoverSuccess, setPushoverSuccess] = useState('')
  const [isSavingPushover, setIsSavingPushover] = useState(false)

  // Telegram identity and display-alias state
  const [telegramAliases, setTelegramAliases] = useState('')
  const [telegramError, setTelegramError] = useState(
    () => new URLSearchParams(window.location.search).get('telegram_error') || ''
  )
  const [telegramSuccess, setTelegramSuccess] = useState(
    () => new URLSearchParams(window.location.search).get('telegram_link') === 'success'
      ? 'Telegram account linked successfully'
      : ''
  )
  const [isSavingTelegram, setIsSavingTelegram] = useState(false)

  // Alert notification preferences state
  const [alertNotificationsEnabled, setAlertNotificationsEnabled] = useState(true)
  const [alertNotificationsError, setAlertNotificationsError] = useState('')
  const [alertNotificationsSuccess, setAlertNotificationsSuccess] = useState('')
  const [isSavingAlertNotifications, setIsSavingAlertNotifications] = useState(false)

  useEffect(() => {
    setPushoverUserKey(user?.pushover_user_key || '')
  }, [user?.pushover_user_key])

  useEffect(() => {
    setTelegramAliases((user?.telegram_nicknames || []).join('\n'))
  }, [user?.telegram_nicknames])

  useEffect(() => {
    setAlertNotificationsEnabled(user?.preferences?.alert_notifications_enabled !== false)
  }, [user?.preferences?.alert_notifications_enabled])

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordError('')
    setPasswordSuccess('')

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match')
      return
    }

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters')
      return
    }

    setIsChangingPassword(true)
    try {
      const data = await changeAccountPassword({
        current_password: currentPassword,
        new_password: newPassword,
      })

      if (!data.ok) {
        setPasswordError(data.error || 'Failed to change password')
        return
      }

      setPasswordSuccess('Password changed successfully')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      setPasswordError('Failed to connect to server')
    } finally {
      setIsChangingPassword(false)
    }
  }

  const handlePushoverSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setPushoverError('')
    setPushoverSuccess('')

    setIsSavingPushover(true)
    try {
      const data = await updateAccountPushover({
        user_key: pushoverUserKey,
      })

      if (!data.ok) {
        setPushoverError(data.error || 'Failed to save Pushover settings')
        return
      }

      setPushoverSuccess('Pushover settings saved successfully')
    } catch (err) {
      setPushoverError('Failed to connect to server')
    } finally {
      setIsSavingPushover(false)
    }
  }

  const handleAlertNotificationsChange = async (enabled: boolean) => {
    const previousValue = alertNotificationsEnabled
    setAlertNotificationsEnabled(enabled)
    setAlertNotificationsError('')
    setAlertNotificationsSuccess('')
    setIsSavingAlertNotifications(true)

    try {
      const data = await updateAccountPreferences({
        alert_notifications_enabled: enabled,
      })

      if (!data.ok) {
        setAlertNotificationsEnabled(previousValue)
        setAlertNotificationsError(data.error || 'Failed to save alert notification settings')
        return
      }

      queryClient.setQueryData<User | null>(['auth', 'me'], (current) => {
        if (!current) return current
        return {
          ...current,
          preferences: {
            ...(current.preferences || {}),
            alert_notifications_enabled: enabled,
          },
        }
      })
      setAlertNotificationsSuccess(
        enabled ? 'Alert notifications enabled' : 'Alert notifications disabled'
      )
    } catch (err) {
      setAlertNotificationsEnabled(previousValue)
      setAlertNotificationsError('Failed to connect to server')
    } finally {
      setIsSavingAlertNotifications(false)
    }
  }

  const handleTelegramAliasesSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setTelegramError('')
    setTelegramSuccess('')
    setIsSavingTelegram(true)
    const aliases = telegramAliases
      .split(/[\n,]/)
      .map((value) => value.trim().replace(/^@/, ''))
      .filter(Boolean)
    try {
      const data = await updateAccountTelegramAliases(aliases)
      if (!data.ok) {
        setTelegramError(data.error || 'Failed to save Telegram aliases')
        return
      }
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
      setTelegramSuccess('Telegram display aliases saved')
    } catch {
      setTelegramError('Failed to connect to server')
    } finally {
      setIsSavingTelegram(false)
    }
  }

  const handleTelegramUnlink = async (userId: string) => {
    setTelegramError('')
    setTelegramSuccess('')
    setIsSavingTelegram(true)
    try {
      const data = await unlinkAccountTelegram(userId)
      if (!data.ok) {
        setTelegramError(data.error || 'Failed to unlink Telegram account')
        return
      }
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
      setTelegramSuccess('Telegram account unlinked')
    } catch {
      setTelegramError('Failed to connect to server')
    } finally {
      setIsSavingTelegram(false)
    }
  }

  return (
    <div className="flex flex-col">
      <Header title="Account" />

      <div className="flex-1 space-y-6 p-6">
        {/* Profile Card */}
        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
            <CardDescription>
              Your account information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary text-2xl font-semibold">
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div>
                <h3 className="text-lg font-medium">{user?.username}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant={isAdmin ? 'default' : 'secondary'}>
                    {user?.role}
                  </Badge>
                  <Badge variant={user?.availability_status === 'online' ? 'success' : 'secondary'}>
                    {user?.availability_status === 'online' ? 'Online' : 'Offline'}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Permissions Card */}
        <Card>
          <CardHeader>
            <CardTitle>Permissions</CardTitle>
            <CardDescription>
              Your current access permissions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isAdmin ? (
              <p className="text-sm text-muted-foreground">
                As an admin, you have full access to all features.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {Object.entries(user?.permissions || {}).map(([perm, enabled]) => (
                  <Badge
                    key={perm}
                    variant={enabled ? 'success' : 'secondary'}
                  >
                    {perm.replace(/_/g, ' ')}
                  </Badge>
                ))}
                {Object.keys(user?.permissions || {}).length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No special permissions granted.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Telegram Identity Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Telegram
            </CardTitle>
            <CardDescription>
              Verified accounts are used for login and bot commands. AKA tags are display-only.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {telegramError && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                {telegramError}
              </div>
            )}
            {telegramSuccess && (
              <div className="bg-green-500/10 text-green-500 text-sm p-3 rounded-md">
                {telegramSuccess}
              </div>
            )}
            <div className="space-y-2">
              {(user?.telegram_accounts || []).map((account) => (
                <div key={account.user_id} className="flex items-center justify-between gap-3 rounded-md border p-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">
                        {account.username ? `@${account.username}` : account.display_name || 'Telegram account'}
                      </span>
                      {account.is_primary && <Badge variant="success">Primary</Badge>}
                    </div>
                    <p className="font-mono text-xs text-muted-foreground">ID {account.user_id}</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    disabled={isSavingTelegram}
                    onClick={() => handleTelegramUnlink(account.user_id)}
                    title="Unlink Telegram account"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {!user?.telegram_accounts?.length && (
                <p className="text-sm text-muted-foreground">No verified Telegram account linked.</p>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                window.location.href = '/api/auth/telegram/start?mode=link&redirect=/account'
              }}
            >
              <Link2 className="mr-2 h-4 w-4" />
              Link another Telegram account
            </Button>
            <form onSubmit={handleTelegramAliasesSave} className="space-y-3 border-t pt-4">
              <div className="space-y-2">
                <Label htmlFor="telegram-aliases">AKA display tags</Label>
                <Textarea
                  id="telegram-aliases"
                  value={telegramAliases}
                  onChange={(e) => setTelegramAliases(e.target.value)}
                  placeholder="market_alias\nnight_shift"
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  These names appear in user representations but cannot sign in or authorize commands.
                </p>
              </div>
              <Button type="submit" disabled={isSavingTelegram}>
                {isSavingTelegram ? 'Saving...' : 'Save Telegram aliases'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Alert Notifications Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Alert Notifications
            </CardTitle>
            <CardDescription>
              Configure app notification preferences for your account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {alertNotificationsError && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                {alertNotificationsError}
              </div>
            )}
            {alertNotificationsSuccess && (
              <div className="bg-green-500/10 text-green-500 text-sm p-3 rounded-md">
                {alertNotificationsSuccess}
              </div>
            )}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="alert-notifications-enabled">New Alert Notifications</Label>
                <p className="text-xs text-muted-foreground">
                  Show a toast notification when a new alert is detected anywhere in the app
                </p>
              </div>
              <Switch
                id="alert-notifications-enabled"
                checked={alertNotificationsEnabled}
                disabled={isSavingAlertNotifications}
                onCheckedChange={handleAlertNotificationsChange}
              />
            </div>
          </CardContent>
        </Card>

        {/* Password Change Card */}
        {user?.has_password !== false && <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Change Password
            </CardTitle>
            <CardDescription>
              Update your account password
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              {passwordError && (
                <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className="bg-green-500/10 text-green-500 text-sm p-3 rounded-md">
                  {passwordSuccess}
                </div>
              )}
              <div className="space-y-2">
                <label htmlFor="current-password" className="text-sm font-medium">
                  Current Password
                </label>
                <div className="relative">
                  <Input
                    id="current-password"
                    type={showPasswords ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="new-password" className="text-sm font-medium">
                  New Password
                </label>
                <Input
                  id="new-password"
                  type={showPasswords ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="confirm-password" className="text-sm font-medium">
                  Confirm New Password
                </label>
                <Input
                  id="confirm-password"
                  type={showPasswords ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPasswords(!showPasswords)}
                >
                  {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  <span className="ml-2">{showPasswords ? 'Hide' : 'Show'} passwords</span>
                </Button>
              </div>
              <Button type="submit" disabled={isChangingPassword}>
                {isChangingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>}

        {/* Pushover Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Pushover Notifications
            </CardTitle>
            <CardDescription>
              Configure Pushover for mobile push notifications
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePushoverSave} className="space-y-4">
              {pushoverError && (
                <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                  {pushoverError}
                </div>
              )}
              {pushoverSuccess && (
                <div className="bg-green-500/10 text-green-500 text-sm p-3 rounded-md">
                  {pushoverSuccess}
                </div>
              )}
              <div className="space-y-2">
                <label htmlFor="pushover-user-key" className="text-sm font-medium">
                  User Key
                </label>
                <Input
                  id="pushover-user-key"
                  type="text"
                  placeholder="Your Pushover user key"
                  value={pushoverUserKey}
                  onChange={(e) => setPushoverUserKey(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Find this in your Pushover account settings
                </p>
              </div>
              <Button type="submit" disabled={isSavingPushover}>
                {isSavingPushover ? 'Saving...' : 'Save Pushover Settings'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Actions Card */}
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              variant="destructive"
              onClick={() => logout()}
              disabled={isLoggingOut}
            >
              <LogOut className="mr-2 h-4 w-4" />
              {isLoggingOut ? 'Logging out...' : 'Logout'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
